/**
 * The video wall.
 *
 * ★★ HISTORY, because it explains every rule below. Every tile here used to be
 *    fabricated: the picture was a stock image URL, the boxes were hardcoded
 *    detections ("fire extinguisher 92%"), latency 28ms / 30fps / "direct-relay"
 *    were constants, the freeze alert was a mock field — and the 7 terminals the
 *    streams belonged to **did not exist**. The banner admitted only that "the
 *    picture and connection mode are placeholders", which is how a page can be
 *    99% fiction and still read as nearly done.
 *
 *    The rewrite before this one deleted all of that and drew "no picture" in
 *    every tile, saying the media path was P3 and unbuilt. That was true when it
 *    was written — and stopped being true on 2026-08-01, when the relay and the
 *    hub's `/api/v1/whep/{device}/{stream}` shipped. The page kept saying "not
 *    wired yet" for a week while the wiring existed: the repo's own recurring
 *    failure (memory: capability-written-but-never-wired). This version connects it.
 *
 * Where each thing on screen comes from now:
 *
 *   real      device list / name / type / online — same useTerminals() as the
 *             devices and overview pages
 *   real      which streams exist — the RELAY's config via /api/v1/video/streams.
 *             Not a list in this file: a copy here would rot the day a camera is
 *             added or a second dog is enrolled.
 *   real      picture — WHEP, media browser <=> relay (see hooks/useWhep.ts)
 *   measured  fps / kbps / resolution / RTT / loss — getStats(), null renders "—"
 *   measured  freeze — framesDecoded standing still, not a flag
 *   gone      "direct or relayed" per tile: for the console it is ALWAYS relayed.
 *             A badge that can only say one thing is decoration, not information.
 *
 * ★ Lazy is the load-bearing property, not an optimisation: a tile pulls video only
 *   while it is rendered and enabled, and the whole wall stops when the tab is
 *   hidden. The dog encodes once and uploads once no matter how many people watch
 *   (that is what the relay bought us); what it must never do is upload for
 *   nobody. Rendering 9 tiles "so switching is snappy" would break exactly that.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { typeLabels, typeIcons } from '@/lib/terminalLabels';
import { useTerminals, useVideoStreams } from '@/hooks/useHubData';
import { NoDataSource, LoadError } from '@/components/feature/DataSource';
import StreamCard, { type TileSpec } from './StreamCard';

type FilterMode = 'all' | 'group' | 'single';

const TERMINAL_TYPES = ['dog', 'glasses', 'phone', 'drone', 'vehicle'] as const;

/** Phones default to one tile — a 2×2 grid of video on a 375px screen is unreadable
 *  (design-fleet-console-implementation.md §6b). */
function defaultLayout(): number {
  return typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 2;
}

export default function LiveLayout() {
  const { terminals, loading: devLoading, error: devError, refetch: devRefetch } = useTerminals();
  const video = useVideoStreams();
  const [layout, setLayout] = useState(defaultLayout);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set(TERMINAL_TYPES));
  const [selectedTerminal, setSelectedTerminal] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Hidden tab => tear every session down. Nobody is looking, so nothing should be
  // travelling over the dog's uplink.
  const [tabVisible, setTabVisible] = useState(() =>
    typeof document === 'undefined' ? true : !document.hidden);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setShowDropdown(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onVis = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // A tile exists where BOTH facts hold: the device is registered, and the relay
  // has a stream for it. The two halves are joined here rather than in either
  // source, so a mismatch on either side is visible below instead of silently
  // producing an empty wall.
  const tiles = useMemo<TileSpec[]>(() => {
    const streamsByDevice = new Map((video.devices ?? []).map(d => [d.deviceId, d.streams]));
    return terminals
      .filter(t => (streamsByDevice.get(t.id)?.length ?? 0) > 0)
      .map(t => ({
        deviceId: t.id,
        deviceName: t.name,
        deviceType: t.type,
        deviceOnline: t.status === 'online',
        streams: streamsByDevice.get(t.id) ?? [],
      }));
  }, [terminals, video.devices]);

  /** Registered, but the relay has no stream configured for it. Named, not hidden. */
  const withoutStreams = useMemo(() => {
    const have = new Set(tiles.map(t => t.deviceId));
    return terminals.filter(t => !have.has(t.id) && !t.revoked);
  }, [terminals, tiles]);

  const filtered = useMemo(() => {
    if (filterMode === 'group') return tiles.filter(t => selectedTypes.has(t.deviceType));
    if (filterMode === 'single') return selectedTerminal ? tiles.filter(t => t.deviceId === selectedTerminal) : [];
    return tiles;
  }, [filterMode, tiles, selectedTypes, selectedTerminal]);

  const visible = filtered.slice(0, layout * layout);
  const selectedObj = selectedTerminal ? tiles.find(t => t.deviceId === selectedTerminal) : null;

  const toggleType = (type: string) => {
    const next = new Set(selectedTypes);
    if (next.has(type)) { if (next.size > 1) next.delete(type); } else next.add(type);
    setSelectedTypes(next);
  };

  // Device registry failing is a different thing from the relay failing, and the
  // page says which one. Only the registry is fatal for this page (no registry =>
  // no way to attribute a stream to anything).
  if (devError) return <LoadError error={devError} onRetry={devRefetch} />;
  if (devLoading || video.loading) {
    return <p className="text-xs text-foreground-400 py-8 text-center">读取设备注册表与中继流清单…</p>;
  }
  if (video.unavailable) {
    return (
      <NoDataSource
        title="没有视频中继, 云端看不到画面"
        reason={`${video.unavailable} —— 中继就是"狗只上传一份、hub 分给 N 个人"那一层。部署: bash 0xman/deploy/deploy_video_relay.sh panda02;局域网内仍可直连狗上的座舱看。`}
        phase="OXMAN_HUB_VIDEO_RELAY"
      />
    );
  }
  if (video.error) {
    return (
      <div className="space-y-3">
        <LoadError error={`中继流清单读不到:${video.error}`} onRetry={video.refetch} />
        <p className="text-[11px] text-foreground-400 leading-relaxed">
          这**不是**"没有摄像头" —— 中继答不上话时台面上一个画面都不该画。
          查 panda02 上的 <code className="font-mono">oxman-video-relay</code>。
        </p>
      </div>
    );
  }
  if (terminals.length === 0) {
    return (
      <NoDataSource
        title="注册表里还没有设备"
        reason="在「设备」页注册一台并把令牌写进它的 auth.env,它就会出现在这里。"
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="flex items-center gap-0.5 bg-background-100 rounded-lg p-0.5">
            {[{ id: 1, label: '1x1', icon: 'ri-layout-line' },
              { id: 2, label: '2x2', icon: 'ri-layout-grid-line' },
              { id: 3, label: '3x3', icon: 'ri-layout-masonry-line' }].map((l) => (
              <button key={l.id} onClick={() => setLayout(l.id)}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md cursor-pointer whitespace-nowrap transition-all ${layout === l.id ? 'bg-background-50 text-foreground-800 border border-background-200/70 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${l.icon} text-xs`} /></span>
                {l.label}
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-background-200/70 mx-0.5 hidden sm:block" />

          <div className="flex items-center gap-0.5 bg-background-100 rounded-lg p-0.5">
            {([{ id: 'all' as FilterMode, label: '全部' },
               { id: 'group' as FilterMode, label: '按组' },
               { id: 'single' as FilterMode, label: '单个' }]).map((m) => (
              <button key={m.id}
                onClick={() => { setFilterMode(m.id); if (m.id === 'single') setSelectedTerminal(null); }}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md cursor-pointer whitespace-nowrap transition-all ${filterMode === m.id ? 'bg-background-50 text-foreground-800 border border-background-200/70 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
                {m.label}
              </button>
            ))}
          </div>

          {filterMode === 'group' && (
            <div className="flex items-center gap-1 ml-1 flex-wrap">
              {TERMINAL_TYPES.map((t) => (
                <button key={t} onClick={() => toggleType(t)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-full cursor-pointer whitespace-nowrap transition-all ${selectedTypes.has(t) ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500 hover:text-foreground-700 hover:bg-background-200/60'}`}>
                  <span className="w-3 h-3 flex items-center justify-center"><i className={`${typeIcons[t]} text-[10px]`} /></span>
                  {typeLabels[t]}
                </button>
              ))}
            </div>
          )}

          {filterMode === 'single' && (
            <div className="relative ml-1" ref={dropdownRef}>
              <button onClick={() => setShowDropdown(!showDropdown)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md cursor-pointer whitespace-nowrap transition-all border ${selectedTerminal ? 'bg-primary-500/10 text-primary-700 border-primary-300/50' : 'bg-background-100 text-foreground-500 border-background-200/50 hover:text-foreground-700 hover:border-background-300/60'}`}>
                {selectedObj ? (
                  <>
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${typeIcons[selectedObj.deviceType] ?? typeIcons.other} text-[10px]`} /></span>
                    {selectedObj.deviceName}
                  </>
                ) : '选择设备'}
                <span className="w-3 h-3 flex items-center justify-center"><i className={`ri-arrow-down-s-line text-xs transition-transform ${showDropdown ? 'rotate-180' : ''}`} /></span>
              </button>
              {showDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-50 max-h-56 overflow-y-auto py-1">
                  {tiles.map((t) => (
                    <button key={t.deviceId}
                      onClick={() => { setSelectedTerminal(t.deviceId); setShowDropdown(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs cursor-pointer transition-colors hover:bg-background-100 ${selectedTerminal === t.deviceId ? 'bg-primary-500/10 text-primary-700' : 'text-foreground-700'}`}>
                      <span className="w-4 h-4 flex items-center justify-center"><i className={`${typeIcons[t.deviceType] ?? typeIcons.other} text-[11px] text-foreground-500`} /></span>
                      <span className="flex-1 text-left">{t.deviceName}</span>
                      <span className="text-[10px] text-foreground-400">{t.streams.length} 路</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1.5" title="有中继流的设备数 / 注册表总数">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
            可看 {tiles.length}/{terminals.length}
          </span>
        </div>
      </div>

      {!tabVisible && (
        // Not a warning — a statement of what the wall is deliberately doing.
        <p className="text-[11px] text-foreground-400 px-3 py-2 rounded-lg bg-background-100">
          标签页在后台, 已断开所有画面 —— 没人看的时候不占设备上行。回到本页会自动重连。
        </p>
      )}

      {tiles.length === 0 && (
        <NoDataSource
          title="中继上还没有任何一路属于已注册设备"
          reason={`中继在跑, 但配置里没有 {设备id}_{流名} 形式的流可以归到注册表里的设备。改 0xman/deploy/go2rtc-relay.yaml 加上那几行, 再跑 deploy_video_relay.sh。${
            video.unassigned.length ? `此刻中继上有归不到设备的流:${video.unassigned.join(', ')}` : ''}`}
        />
      )}

      {filtered.length === 0 && tiles.length > 0 && filterMode !== 'all' && (
        <div className="flex flex-col items-center justify-center py-16 text-foreground-400">
          <span className="w-12 h-12 flex items-center justify-center mb-3"><i className="ri-filter-off-line text-3xl" /></span>
          <p className="text-sm font-medium">没有匹配的设备</p>
          <button
            onClick={() => { setFilterMode('all'); setSelectedTerminal(null); setSelectedTypes(new Set(TERMINAL_TYPES)); }}
            className="mt-3 px-3 py-1.5 text-xs font-medium rounded-md bg-background-100 text-foreground-600 hover:text-foreground-800 hover:bg-background-200/60 cursor-pointer whitespace-nowrap transition-all">
            重置筛选
          </button>
        </div>
      )}

      {filtered.length > 0 && (
        <>
          <div className="flex items-center justify-between text-xs text-foreground-400">
            <span>
              {filterMode === 'all' && `${filtered.length} 台设备有画面可看`}
              {filterMode === 'group' && `已选 ${selectedTypes.size} 组,共 ${filtered.length} 台`}
              {filterMode === 'single' && selectedObj && `仅显示 ${selectedObj.deviceName}`}
            </span>
            {filtered.length > visible.length && <span>{visible.length} / {filtered.length}</span>}
          </div>
          <div className={`grid gap-2 ${layout === 1 ? 'grid-cols-1' : layout === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
            {/* Only the tiles actually on screen get a peer connection — see the
                header comment on why that is a contract and not a tweak. */}
            {visible.map((t) => (
              <StreamCard key={t.deviceId} tile={t} enabled={tabVisible} />
            ))}
          </div>
        </>
      )}

      {withoutStreams.length > 0 && (
        <p className="text-[11px] text-foreground-400 leading-relaxed pt-1">
          注册了但中继上没有它的流:{withoutStreams.map((t) => t.name).join('、')}
          <span className="text-foreground-300"> —— 不是"离线", 是 go2rtc-relay.yaml 里没写这几路。</span>
        </p>
      )}

      {video.unassigned.length > 0 && tiles.length > 0 && (
        <p className="text-[11px] text-amber-700 leading-relaxed">
          中继上有 {video.unassigned.length} 路归不到任何注册设备:
          <span className="font-mono"> {video.unassigned.join(', ')}</span>
          <span className="text-amber-600/80"> —— 设备已吊销或流名前缀写错了。</span>
        </p>
      )}

      <p className="text-[11px] text-foreground-400 leading-relaxed pt-1">
        画面走 hub 中继:设备只编码并上传一份, 中继分发给 N 个观看者(所以每格右下"上游"恒为 1)。
        信令经 <code className="font-mono">/api/v1/whep/{'{设备}'}/{'{流}'}</code> 带令牌鉴权, 媒体是浏览器⇄中继直连。
        没有观看者时中继不连设备, 设备上行为 0。
      </p>
    </div>
  );
}
